import { getAiAnswerExplanationQuery } from "@domain/ai_answers"
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  AiAnswerExplanation,
  AiMessageSource,
  AiMessageSteps
} from "__generated__/graphql"

import {
  COMPLIANCE_LABELS,
  decisiveStep,
  ExplanationTone,
  OUTCOME_LABELS,
  REASON_LABELS,
  SOURCE_TYPE_LABELS,
  SUGGESTION_LABELS
} from "./answerExplanation.labels"

const TONE: Record<
  ExplanationTone,
  { fg: string; bg: string; border: string }
> = {
  success: { fg: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  warning: { fg: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  error: { fg: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
  neutral: { fg: "#475569", bg: "#f8fafc", border: "#e2e8f0" }
}

const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace"

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.secondary",
        mb: 0.5
      }}
    >
      {children}
    </Typography>
  )
}

function Quote({ label, text }: { label: string; text: string }) {
  return (
    <Box sx={{ borderLeft: "3px solid #cbd5e1", pl: 1.25, py: 0.25, mb: 1 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {text}
      </Typography>
    </Box>
  )
}

function SourceRow({ source }: { source: AiMessageSource }) {
  const type = String(source.type ?? "")
  return (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "baseline", py: 0.25 }}>
      <Chip
        size="small"
        label={SOURCE_TYPE_LABELS[type] ?? type}
        sx={{ height: 18, fontSize: 10.5, flexShrink: 0 }}
      />
      <Typography
        variant="body2"
        sx={{ minWidth: 0, overflowWrap: "anywhere" }}
      >
        {source.subTopic || source.topic || String(source.id)}
      </Typography>
    </Box>
  )
}

/**
 * The verdict the user panel shows its tenant, with the admin's extras: the raw codes, the step it
 * was read from, and every target id — so "why did it not answer" is one tab, not a step hunt.
 */
export default function AnswerExplanationView({
  runId,
  steps,
  onJumpToStep,
  onShowSources
}: {
  runId: string
  steps: AiMessageSteps[] | undefined
  onJumpToStep: (stepId: string) => void
  onShowSources?: () => void
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-ai-answer-explanation", runId],
    queryFn: async () => {
      const response = await getAiAnswerExplanationQuery(runId)
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage ?? "explanation failed")
      }
      const explanation: AiAnswerExplanation = response.data
      return explanation
    }
  })

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Açıklama alınamadı.
      </Typography>
    )
  }

  const outcome = OUTCOME_LABELS[data.outcome]
  const tone = TONE[outcome.tone]
  const reason = data.reason ? REASON_LABELS[data.reason] : null
  const verdictStep = decisiveStep(steps, data.reason)
  const cited = data.sources.filter((source) => source.cited)
  const seenCount = data.sources.length - cited.length
  const searches = data.knowledgeSearches.filter((search) => search.query)
  const facts = [
    data.surface,
    data.roleName && `${data.roleName}${data.role ? ` (${data.role})` : ""}`,
    data.agentAction && `action: ${data.agentAction}`,
    data.confidence && `emin: ${data.confidence}`,
    data.activeSkill && `skill: ${data.activeSkill}`,
    data.handedOff && "devredildi",
    data.dmSent && "DM gitti",
    data.handoffDisabled && "kanalda devir kapalı",
    data.engineVersion
  ].filter((fact): fact is string => !!fact)

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: `1px solid ${tone.border}`,
          bgcolor: tone.bg
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: tone.fg, fontWeight: 700, flex: 1 }}
          >
            {outcome.label}
          </Typography>
          {verdictStep && (
            <Button
              size="small"
              onClick={() => onJumpToStep(String(verdictStep._id))}
              sx={{ textTransform: "none", fontSize: 12, py: 0 }}
            >
              #{verdictStep.stepList} {String(verdictStep.step)} adımına git
            </Button>
          )}
        </Box>
        {reason && (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
              {reason.title}{" "}
              <Box
                component="span"
                sx={{ fontFamily: MONO, fontSize: 11, color: "text.secondary" }}
              >
                {data.reason}
              </Box>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reason.detail}
            </Typography>
          </>
        )}
        {data.groupedIntoRunId && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Bu mesaj başka bir run içinde cevaplandı; açıklama{" "}
            <Box component="span" sx={{ fontFamily: MONO }}>
              {data.groupedIntoRunId}
            </Box>{" "}
            run&apos;ına ait. Adımlar bu mesajın kendi run&apos;ını gösteriyor.
          </Typography>
        )}
        {facts.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {facts.map((fact) => (
              <Chip
                key={fact}
                size="small"
                label={fact}
                variant="outlined"
                sx={{ height: 20, fontSize: 11, bgcolor: "background.paper" }}
              />
            ))}
          </Box>
        )}
      </Box>

      {(data.handoffReason ||
        data.agentNote ||
        data.knowledgeGap ||
        searches.length > 0 ||
        data.complianceViolations.length > 0) && (
        <Box>
          <SectionTitle>Ajanın kararı</SectionTitle>
          {data.handoffReason && (
            <Quote label="handoffReason" text={data.handoffReason} />
          )}
          {data.agentNote && data.agentNote !== data.handoffReason && (
            <Quote label="notes" text={data.agentNote} />
          )}
          {data.knowledgeGap && (
            <Quote label="knowledgeGap" text={data.knowledgeGap} />
          )}
          {data.complianceViolations.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Uyum ihlalleri
              </Typography>
              {data.complianceViolations.map((violation) => (
                <Typography
                  key={`${violation.code}-${violation.surface ?? ""}`}
                  variant="body2"
                >
                  • {COMPLIANCE_LABELS[violation.code] ?? violation.code}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontFamily: MONO,
                      fontSize: 11,
                      color: "text.secondary"
                    }}
                  >
                    {violation.code}
                    {violation.surface ? ` · ${violation.surface}` : ""}
                  </Box>
                </Typography>
              ))}
            </Box>
          )}
          {searches.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                search_knowledge
              </Typography>
              {searches.map((search, index) => (
                <Box
                  key={`${search.query}-${index}`}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    py: 0.25
                  }}
                >
                  <Typography variant="body2" noWrap title={search.query}>
                    {search.query}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: MONO,
                      flexShrink: 0,
                      color: search.hits > 0 ? "success.main" : "warning.main"
                    }}
                  >
                    {search.hits > 0 ? `${search.hits} sonuç` : search.status}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {data.suggestions.length > 0 && (
        <Box>
          <SectionTitle>Tenant&apos;a gösterilen öneriler</SectionTitle>
          {data.suggestions.map((suggestion) => (
            <Box key={suggestion.code} sx={{ py: 0.25 }}>
              <Typography variant="body2">
                • {SUGGESTION_LABELS[suggestion.code]}{" "}
                <Box
                  component="span"
                  sx={{
                    fontFamily: MONO,
                    fontSize: 11,
                    color: "text.secondary"
                  }}
                >
                  {suggestion.code}
                  {suggestion.targetId ? ` · ${suggestion.targetId}` : ""}
                </Box>
              </Typography>
              {suggestion.question && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 1.5, fontStyle: "italic" }}
                >
                  “{suggestion.question}”
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box>
        <SectionTitle>Referans aldıkları · {cited.length}</SectionTitle>
        {!data.hasCitations && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Bu run citations alanından önce — yalnızca kesin kullanılanlar
            (kart, action, skill) işaretli.
          </Typography>
        )}
        {cited.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Ajan belirli bir kaynağa dayanmadı.
          </Typography>
        ) : (
          cited.map((source, index) => (
            <SourceRow key={`${source.id}-${index}`} source={source} />
          ))
        )}
        {seenCount > 0 && onShowSources && (
          <Button
            size="small"
            onClick={onShowSources}
            sx={{ textTransform: "none", fontSize: 12, mt: 0.5, px: 0 }}
          >
            Gördüğü diğer {seenCount} kaynak → Kullanılan kaynaklar
          </Button>
        )}
      </Box>
    </Box>
  )
}
